// src/components/lesson/diagrams/Sphere.tsx
//
// Sphere for SAT Volume questions: V = (4/3)πr³ (on the Digital SAT reference
// sheet). Drawn as a circle with an equator ellipse (front half solid, back
// half dashed) for a 3D read, plus a labeled radius.
//
// Params (all optional):
//   radiusLabel?: string ("r"/"3"), diameterLabel?: string
//   label?: string

type Params = {
  radiusLabel?: string
  diameterLabel?: string
  label?: string
}

const SIZE = 240
const PAD = 30
const CX = SIZE / 2
const CY = SIZE / 2
const R = SIZE / 2 - PAD
const EQ_SQUISH = 0.26
const stroke = 'rgb(34 211 238)'
const fill = 'rgb(34 211 238 / 0.08)'
const labelColor = 'rgb(251 191 36)'
const strokeW = 1.8

export default function Sphere({ params }: { params: Record<string, unknown> }) {
  const p = params as Params
  const ry = R * EQ_SQUISH

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label={p.label ?? 'Sphere'}
      className="my-3 w-full max-w-xs rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Sphere outline */}
      <circle cx={CX} cy={CY} r={R} fill={fill} stroke={stroke} strokeWidth={strokeW} />

      {/* Equator — back half dashed, front half solid (3D cue) */}
      <path
        d={`M ${CX - R} ${CY} A ${R} ${ry} 0 0 0 ${CX + R} ${CY}`}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeW * 0.8}
      />
      <path
        d={`M ${CX - R} ${CY} A ${R} ${ry} 0 0 1 ${CX + R} ${CY}`}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeW * 0.6}
        strokeDasharray="4 3"
      />

      {/* Radius (or diameter) */}
      {p.diameterLabel !== undefined ? (
        <>
          <line
            x1={CX - R}
            y1={CY}
            x2={CX + R}
            y2={CY}
            stroke={labelColor}
            strokeWidth={strokeW * 0.8}
          />
          <circle cx={CX} cy={CY} r="2" fill={labelColor} />
          <text
            x={CX}
            y={CY - 6}
            fontSize="12"
            fontWeight="600"
            textAnchor="middle"
            fill={labelColor}
          >
            {p.diameterLabel}
          </text>
        </>
      ) : (
        <>
          <circle cx={CX} cy={CY} r="2" fill={labelColor} />
          <line
            x1={CX}
            y1={CY}
            x2={CX + R}
            y2={CY}
            stroke={labelColor}
            strokeWidth={strokeW * 0.8}
          />
          <text
            x={CX + R / 2}
            y={CY - 6}
            fontSize="12"
            fontWeight="600"
            textAnchor="middle"
            fill={labelColor}
          >
            {p.radiusLabel ?? 'r'}
          </text>
        </>
      )}
    </svg>
  )
}
